import React from 'react';
import classnames from 'classnames';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import ThreadPagination from '../../_partials/ThreadPagination';
import { MIN_COMMENT_COUNT_TO_SHOW_BOTTOM_POST } from '../../constants';
import ThreadHeader from '../../_partials/ThreadHeader';
import ThreadPostbox from '../../_partials/ThreadPostbox';
import ThreadStack from '../../_partials/ThreadStack';
import { Props as CommentProps } from '../../../Comment/presets/Comment';
import { ThreadProps } from '../../types';
import { BiData, LoadMoreCommentsClickBiEvent } from '@wix/da-bi/pkg/events';

import s from './SimpleThread.scss';

export interface Props extends ThreadProps {
  streamId: string;
  itemCount: number;
  isFetching?: boolean;

  maxDepthOverride?: number;
  extraInHeader?: React.ReactNode;
  commentComponent?: React.ComponentType<CommentProps>;
  replyIndentModifier?: number;

  className?: string;
  headerClassName?: string;
  scrollableClassName?: string;
  commentBoxWrapperClassName?: string;
  threadStackClassName?: string;
  commentClassName?: string;
  commentWrapperClassName?: string;
  viewRepliesClassName?: string;
  isSticky?: boolean;
}

const SimpleThread: React.FC<Props> = ({
  commentableItemId,
  commentableTypeId,
  parentId,
  parentComment,
  parentCommentRef,
  className,
  postbox,
  itemCount,
  maxCommentsShown,
  statsAndControls,
  extraInHeader,
  streamId,
  allowSelfInit,
  isFetching,
  isInfinite,
  commentComponent,
  scrollableClassName,
  commentClassName,
  commentWrapperClassName,
  viewRepliesClassName,
  maxDepthOverride,
  replyIndentModifier,
  commentBoxWrapperClassName,
  threadStackClassName,
  headerClassName,
  isSticky,
}) => {
  const isTopPostboxVisible = postbox === 'top' || postbox === 'both';
  const isTopmostPostboxVisible = postbox === 'topmost';
  const isBottomPostboxVisible =
    postbox === 'bottom' ||
    (postbox === 'both' && itemCount >= MIN_COMMENT_COUNT_TO_SHOW_BOTTOM_POST);
  const isNotLimitingComments = !maxCommentsShown;
  const postBoxProps = {
    commentableItemId,
    commentableTypeId,
    parentId,
    parentComment,
    isSticky,
    className: commentBoxWrapperClassName,
  };

  return (
    <div className={className}>
      {isTopmostPostboxVisible && (
        <ThreadPostbox {...postBoxProps} position="topmost" />
      )}
      {statsAndControls !== 'off' && (
        <ThreadHeader
          commentableItemId={commentableItemId}
          commentableTypeId={commentableTypeId}
          parentId={parentId}
          withOrder={['on', 'order'].includes(statsAndControls as string)}
          withTitle={['on', 'title'].includes(statsAndControls as string)}
          className={classnames(s['header'], headerClassName)}
        >
          {extraInHeader}
        </ThreadHeader>
      )}
      <div className={classnames(s['scrollable'], scrollableClassName)}>
        <div>
          {isTopPostboxVisible && (
            <ThreadPostbox {...postBoxProps} position="top" />
          )}
          {isNotLimitingComments && (
            <ThreadPagination
              direction="prev"
              streamId={streamId}
              biData={BiData<LoadMoreCommentsClickBiEvent>({
                evid: 182,
                is_load_previous: 1,
                itemid: commentableItemId,
                typeid: commentableTypeId,
              })}
            />
          )}
          <ThreadStack
            className={threadStackClassName}
            commentableItemId={commentableItemId}
            commentableTypeId={commentableTypeId}
            parentId={parentId}
            maxCommentsShown={maxCommentsShown}
            commentComponent={commentComponent}
            parentComment={parentComment}
            parentCommentRef={parentCommentRef}
            allowSelfInit={allowSelfInit}
            maxDepthOverride={maxDepthOverride}
            commentClassName={commentClassName}
            commentWrapperClassName={commentWrapperClassName}
            viewRepliesClassName={viewRepliesClassName}
            replyIndentModifier={replyIndentModifier}
          />
          {isFetching && <LoadingIndicator view="llama" />}
          {isNotLimitingComments && (
            <ThreadPagination
              direction="next"
              className={s['load-more']}
              isInfinite={isInfinite}
              streamId={streamId}
              biData={BiData<LoadMoreCommentsClickBiEvent>({
                evid: 182,
                itemid: commentableItemId,
                typeid: commentableTypeId,
              })}
            />
          )}
        </div>
      </div>
      {isBottomPostboxVisible && (
        <ThreadPostbox {...postBoxProps} position="bottom" />
      )}
    </div>
  );
};

SimpleThread.displayName = 'SimpleThread';

export default SimpleThread;
